import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const SA_DARK = '#0F172A';
const SA_ACCENT = '#6366F1';

export default function SuperAdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setError('');
    setLoading(true);

    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });

    if (authError || !data.user) {
      setLoading(false);
      setError(authError?.message || 'Authentication failed.');
      return;
    }

    // Verify this user is actually a super admin
    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', data.user.id)
      .maybeSingle();

    setLoading(false);

    if (!profile || profile.role !== 'super_admin') {
      await supabase.auth.signOut();
      setError('Access denied. This portal is for platform administrators only.');
      return;
    }

    navigate('/super-admin');
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: SA_DARK }}>
      {/* Left panel */}
      <div className="hidden lg:flex flex-col w-[44%] relative overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/1085df96d87b13f423665fb3f5c85ad1.jpg"
            alt="Platform"
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(15,23,42,0.8) 100%)' }} />

        <div className="relative z-10 flex flex-col h-full p-12">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white font-black text-sm" style={{ backgroundColor: SA_ACCENT }}>
              SA
            </div>
            <div>
              <p className="text-white font-bold text-base leading-none">TravelCRM</p>
              <p className="text-xs font-semibold mt-0.5" style={{ color: SA_ACCENT }}>Platform Control Center</p>
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 w-fit" style={{ backgroundColor: 'rgba(99,102,241,0.15)', color: SA_ACCENT }}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              All Systems Operational
            </div>

            <h2 className="text-4xl font-black text-white leading-tight mb-4">
              Super Admin<br />Command Center
            </h2>
            <p className="text-white/50 text-sm leading-relaxed max-w-xs">
              Full platform oversight, agency management, billing, and user control — accessible to authorized administrators only.
            </p>

            <div className="mt-10 space-y-3">
              {[
                { icon: 'ri-building-4-line', label: 'Manage all tenant agencies' },
                { icon: 'ri-group-line', label: 'Full user administration' },
                { icon: 'ri-price-tag-3-line', label: 'Billing & plan control' },
                { icon: 'ri-shield-check-line', label: 'Platform security settings' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(99,102,241,0.2)' }}>
                    <i className={`${item.icon} text-sm`} style={{ color: SA_ACCENT }} />
                  </div>
                  <span className="text-white/60 text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Platform stats */}
          <div className="flex gap-8">
            {[
              { value: '16', label: 'Active Agencies' },
              { value: '143', label: 'Total Users' },
              { value: '99.9%', label: 'Uptime' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl font-black text-white">{s.value}</p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Mobile header */}
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-black text-sm" style={{ backgroundColor: SA_ACCENT }}>
              SA
            </div>
            <div>
              <p className="text-white font-bold text-sm">TravelCRM</p>
              <p className="text-xs" style={{ color: SA_ACCENT }}>Platform Admin</p>
            </div>
          </div>

          {/* Access badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6" style={{ backgroundColor: 'rgba(99,102,241,0.15)', color: SA_ACCENT }}>
            <i className="ri-shield-keyhole-line" />
            Restricted Access — Authorized Personnel Only
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-black text-white">Platform Admin Login</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Sign in with your administrator credentials</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="rounded-xl px-4 py-3 flex items-start gap-2 border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.2)' }}>
                <i className="ri-error-warning-line text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Admin Email</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-sm text-white focus:outline-none transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  placeholder="admin@platform.com"
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Password</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }} />
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 rounded-xl text-sm text-white focus:outline-none transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer transition-colors"
                  style={{ color: 'rgba(255,255,255,0.3)' }}
                >
                  <i className={`${showPass ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl text-white font-bold text-sm cursor-pointer disabled:opacity-60 transition-all flex items-center justify-center gap-2 whitespace-nowrap mt-6"
              style={{ backgroundColor: SA_ACCENT }}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <i className="ri-shield-check-line" />
                  Access Admin Panel
                </>
              )}
            </button>
          </form>

          {/* Security note */}
          <div className="mt-8 rounded-xl px-4 py-3 border" style={{ backgroundColor: 'rgba(99,102,241,0.08)', borderColor: 'rgba(99,102,241,0.15)' }}>
            <div className="flex items-start gap-2">
              <i className="ri-lock-2-line text-sm mt-0.5 flex-shrink-0" style={{ color: SA_ACCENT }} />
              <div>
                <p className="text-xs font-semibold mb-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Secure Access</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>This portal is for platform administrators only. Unauthorized access attempts are logged and monitored.</p>
              </div>
            </div>
          </div>

          {/* Back to agency login */}
          <div className="mt-5 text-center">
            <a href="/login" className="text-xs transition-colors cursor-pointer hover:underline" style={{ color: 'rgba(255,255,255,0.3)' }}>
              ← Back to Agency Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
